import React from 'react';
import {
  Box, Container, Typography, Grid, Divider, Stack,
} from '@mui/material';
import {
  SettingsOutlined, FolderOutlined, SyncAlt, AccessTime, ShowChart,
  AccountTreeOutlined, ShieldOutlined, GroupOutlined, ExtensionOutlined,
  SecurityOutlined, HubOutlined, VerifiedUserOutlined, NorthEast,
} from '@mui/icons-material';
import { C, Section } from './theme';

const featureSections = [
  {
    category: 'Performance & scale',
    items: [
      {
        title: 'AMD EPYC processors',
        desc: 'Modern cores deliver low-latency compute for parallel jobs so your vps stays responsive under load.',
        icon: <SettingsOutlined sx={{ color: '#2563EB', fontSize: 20 }} />,
      },
      {
        title: 'NVMe SSD storage',
        desc: 'High-throughput storage accelerates logs, queues, and databases for snappy virtual servers.',
        icon: <FolderOutlined sx={{ color: '#10B981', fontSize: 20 }} />,
      },
      {
        title: 'Unmetered bandwidth',
        desc: 'Move data freely without surprise overage fees.',
        icon: <SyncAlt sx={{ color: '#0EA5E9', fontSize: 20 }} />,
      },
      {
        title: 'Add resources on demand',
        desc: 'Scale CPU/RAM and disk space as needs grow—no rebuild required.',
        icon: <AccessTime sx={{ color: '#3B82F6', fontSize: 20 }} />,
      },
      {
        title: 'Handles traffic spikes',
        desc: 'Keep flows steady during launches; capacity scales to absorb concurrency.',
        icon: <ShowChart sx={{ color: '#6366F1', fontSize: 20 }} />,
      },
    ],
  },
  {
    category: 'Control & access',
    items: [
      {
        title: 'Full root access (SSH)',
        desc: 'Configure services, tune kernels, and automate deploys on your virtual machine.',
        icon: <AccountTreeOutlined sx={{ color: '#F59E0B', fontSize: 20 }} />,
      },
      {
        title: 'Remote access',
        desc: 'Secure SSH/SFTP for quick fixes and quick access to logs.',
        icon: <ShieldOutlined sx={{ color: '#10B981', fontSize: 20 }} />,
      },
      {
        title: 'Management panel',
        desc: 'Install your own control panel for painless DNS, users, and backups.',
        icon: <GroupOutlined sx={{ color: '#2563EB', fontSize: 20 }} />,
      },
      {
        title: 'Install custom software',
        desc: 'Run whatever software your workflow requires.',
        icon: <ExtensionOutlined sx={{ color: '#0EA5E9', fontSize: 20 }} />,
      },
    ],
  },
  {
    category: 'Security & reliability',
    items: [
      {
        title: 'DDoS protection',
        desc: 'Network-level filtering shields your vps hosting from volumetric attacks.',
        icon: <SecurityOutlined sx={{ color: '#10B981', fontSize: 20 }} />,
      },
      {
        title: 'World Class Infrastructure',
        desc: 'Actively monitored 24/7 with 99.99% uptime guarantee.',
        icon: <HubOutlined sx={{ color: '#2563EB', fontSize: 20 }} />,
      },
      {
        title: 'High availability ready',
        desc: 'Architect near zero downtime with distributed nodes and health checks.',
        icon: <VerifiedUserOutlined sx={{ color: '#0EA5E9', fontSize: 20 }} />,
      },
    ],
  },
];

const VpsFeatures = () => (
  <Section id="features" sx={{ background: '#FFFFFF', py: { xs: 8, md: 12 } }}>
    <Container maxWidth="lg">

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography variant="h2" sx={{
          fontWeight: 800,
          fontSize: { xs: '2rem', sm: '2.5rem', md: '3.1rem' },
          color: '#0F172A',
          letterSpacing: '-0.03em',
          mb: 2,
        }}>
          Included with all our VPS hosting plans
        </Typography>
        <Typography sx={{
          color: '#475569',
          fontSize: { xs: '0.98rem', md: '1.05rem' },
          maxWidth: 820,
          mx: 'auto',
          lineHeight: 1.65,
        }}>
          A full featured stack for developers. Everything you need to run resource-intensive applications on a virtual private server: fast performance, complete control, and rock-solid reliability.
        </Typography>
      </Box>

      {/* Feature Sections with Dividers */}
      <Stack spacing={6}>
        {featureSections.map((sec, idx) => (
          <Box key={sec.category}>
            {/* MUI v6 Grid size */}
            <Grid container spacing={{ xs: 3, md: 4 }} alignItems="flex-start">

              {/* Left Column: Category Name */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Typography sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.2rem', md: '1.35rem' },
                  color: '#0F172A',
                  letterSpacing: '-0.02em',
                }}>
                  {sec.category}
                </Typography>
              </Grid>

              {/* Right Column: Items Grid */}
              <Grid size={{ xs: 12, md: 9 }}>
                <Grid container spacing={{ xs: 3, md: 4 }}>
                  {sec.items.map((item) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.title}>
                      <Box sx={{ pr: 1 }}>
                        {/* Title Row with Icon & Arrow */}
                        <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                              {item.icon}
                            </Box>
                            <Typography sx={{
                              fontWeight: 800,
                              fontSize: '0.95rem',
                              color: '#0F172A',
                              lineHeight: 1.25,
                            }}>
                              {item.title}
                            </Typography>
                          </Stack>
                          <NorthEast sx={{ fontSize: 14, color: '#94A3B8', flexShrink: 0 }} />
                        </Stack>

                        {/* Description */}
                        <Typography sx={{
                          color: '#475569',
                          fontSize: '0.86rem',
                          lineHeight: 1.6,
                        }}>
                          {item.desc}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

            </Grid>

            {/* Horizontal Divider Line */}
            {idx < featureSections.length - 1 && (
              <Divider sx={{ borderColor: '#E2E8F0', mt: 6 }} />
            )}
          </Box>
        ))}
      </Stack>

    </Container>
  </Section>
);

export default VpsFeatures;
